import {
  Button,
  Container,
  Divider,
  Flex,
  LoadingOverlay,
} from '@mantine/core';
import { useFetch } from '@mantine/hooks';
import { notifications } from '@mantine/notifications';
import { Download } from 'lucide-react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import PageHeader from '../components/shared/PageHeader';
import { config } from '../config/env';
import {
  ProCourseCertificateByTemplate,
  type ProCourseCertificateByTemplateProps,
} from '../certificateTemplates/certificateTemplateRegistry';
import {
  getCertificateTemplateId,
  resolveCertificateBranding,
} from '../certificateTemplates/courseCertificationDetails';
import { pickCourseCertificateValue } from '../certificateTemplates/courseCertificateTemplateByCourseId';
import { ICourse } from '../interfaces/ICourse.tsx';
import { DEFAULT_CERTIFICATE_TEMPLATE_ID } from '../types/certificateTemplate';
import { useAuthInitialized, useAuthUser } from '../providers/AuthProvider';
import {
  getParticipantCertificateParts,
  getParticipantFullName,
  getParticipantGivenFamilyNames,
} from '../utils/participantDisplayName';
import {
  buildCertificateMetaLine,
  formatCertificateDateDdMmYyyy,
  formatCertificatePassedDate,
  resolveItalianDoctorHonorific,
} from '../utils/proCourseCertificateMeta';
import {
  buildCertificatePdfFilename,
  exportElementAsRasterPdf,
  isProCourseCertificateEligible,
} from '../utils/proCourseCertificateExport';

type UserCoursePayload = { data: ICourse & ProCourseCertificateFields };

type ProCourseCertificateFields = {
  completed_at?: string | null;
  review?: unknown;
  passed_at?: string | null;
  provider?: string;
  provider_logo?: string;
  slug?: string;
};

export function ProCourseCertificatePage() {
  const { t } = useTranslation();
  const { id } = useParams();
  const navigate = useNavigate();
  const authInitialized = useAuthInitialized();
  const user = useAuthUser();
  const certificateRef = useRef<HTMLDivElement>(null);
  const [exporting, setExporting] = useState(false);

  const { data, loading } = useFetch<UserCoursePayload>(
    `${config.API_URL}/services/userCourses/${id}`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  /** Stesso elenco usato in catalogo/admin: spesso include `certificate` anche se il GET singolo no. */
  const { data: userCoursesListPayload } = useFetch<{ data: ICourse[] }>(
    `${config.API_URL}/services/userCourses`,
    {
      credentials: 'include',
      headers: {
        'Content-Type': 'application/json',
      },
    }
  );

  const course = data?.data;

  const numericCourseId = id != null ? Number(id) : Number.NaN;

  const courseFromUserCoursesList = useMemo(() => {
    const list = userCoursesListPayload?.data;
    if (list == null || !Number.isFinite(numericCourseId)) return undefined;
    return list.find(c => c.id === numericCourseId);
  }, [userCoursesListPayload, numericCourseId]);

  const courseCertificateValue = useMemo(() => {
    if (course == null) return undefined;
    const fromDetail = pickCourseCertificateValue(course);
    if (fromDetail != null) return fromDetail;
    return pickCourseCertificateValue(courseFromUserCoursesList);
  }, [course, courseFromUserCoursesList]);

  useEffect(() => {
    if (loading || !course) return;
    if (!isProCourseCertificateEligible(course)) {
      notifications.show({
        color: 'yellow',
        title: 'Certificato non ancora disponibile',
        message:
          'Per il certificato devi prima concludere il corso: tutti i contenuti, questionario di gradimento e test superato.',
      });
      navigate(`/proCourses/${id}`, { replace: true });
    }
  }, [loading, course, id, navigate]);

  const participantFullName = useMemo(
    () => getParticipantFullName(user),
    [user]
  );

  const participantParts = useMemo(
    () => getParticipantCertificateParts(user),
    [user]
  );

  useEffect(() => {
    if (!authInitialized || loading || !course || !user) return;
    if (!isProCourseCertificateEligible(course)) return;
    if (participantFullName) return;
    notifications.show({
      color: 'red',
      title: 'Nome non disponibile',
      message:
        'Il certificato usa titolo (se presente), nome e cognome del profilo. Completa i dati e riprova.',
    });
    navigate(`/proCourses/${id}`, { replace: true });
  }, [
    authInitialized,
    loading,
    course,
    user,
    participantFullName,
    id,
    navigate,
  ]);

  const passedDateLabel =
    course?.passed_at != null
      ? formatCertificatePassedDate(course.passed_at)
      : '—';

  const metaLine = useMemo(
    () => (course ? buildCertificateMetaLine(course) : ''),
    [course]
  );

  const templateId = useMemo(
    () =>
      course != null
        ? getCertificateTemplateId(courseCertificateValue)
        : DEFAULT_CERTIFICATE_TEMPLATE_ID,
    [course, courseCertificateValue]
  );
  const usesDefaultCertificateLayout =
    templateId === 'cert1' || templateId === 'cert3';

  const { signatureSlots, showIrisLogo } = useMemo(() => {
    if (course == null || !usesDefaultCertificateLayout) {
      return { signatureSlots: [], showIrisLogo: false };
    }
    return resolveCertificateBranding(course.id, courseCertificateValue);
  }, [course, courseCertificateValue, usesDefaultCertificateLayout]);

  const certificateProps =
    useMemo((): ProCourseCertificateByTemplateProps | null => {
      if (course == null || participantParts == null || user == null) {
        return null;
      }
      if (templateId === 'cert2') {
        if (course.passed_at == null) return null;
        const passedIso = course.passed_at;
        const dateDdMmYyyy = formatCertificateDateDdMmYyyy(passedIso);
        const { article, honorific } = resolveItalianDoctorHonorific(
          participantParts.title
        );
        const { givenName, familyName } = getParticipantGivenFamilyNames(
          user,
          participantParts.nameLine
        );
        const fiscalRaw = user.fiscal_code;
        const fiscalCode =
          typeof fiscalRaw === 'string' ? fiscalRaw.trim() : '';
        const ecmNum = Number(course.ecm);
        const ecmCreditsLabel = Number.isFinite(ecmNum)
          ? String(ecmNum)
          : String(course.ecm ?? '');
        return {
          templateId: 'cert2',
          courseName: course.name ?? '',
          courseHeldDateLabel: dateDdMmYyyy,
          article,
          honorific,
          participantGivenName: givenName,
          participantFamilyName: familyName,
          fiscalCode,
          participationDateLabel: dateDdMmYyyy,
          ecmCreditsLabel,
          professionLine: '5 Psicologo',
          disciplineLine: '77 Psicoterapia',
          issuerCityDateLabel: `Milano, lì ${dateDdMmYyyy}`,
        };
      }
      return {
        templateId,
        courseName: course.name ?? '',
        passedDateLabel,
        metaLine,
        participantTitle: participantParts.title,
        participantNameLine: participantParts.nameLine,
        providerLogoUrl: course.provider_logo,
        signatureSlots,
        showIrisLogo,
      };
    }, [
      course,
      metaLine,
      participantParts,
      passedDateLabel,
      signatureSlots,
      showIrisLogo,
      templateId,
      user,
    ]);

  const handleSavePdf = useCallback(async () => {
    const el = certificateRef.current;
    if (!el || !course) return;
    setExporting(true);
    try {
      await exportElementAsRasterPdf(
        el,
        buildCertificatePdfFilename(course.id, course.slug)
      );
    } catch (e) {
      const message =
        e instanceof Error ? e.message : 'Impossibile salvare il PDF.';
      notifications.show({
        color: 'red',
        title: 'Errore',
        message,
      });
    } finally {
      setExporting(false);
    }
  }, [course]);

  const eligible =
    course != null &&
    isProCourseCertificateEligible(course) &&
    participantFullName != null;

  const backUrl = id != null ? `/proCourses/${id}` : '/proCourses';

  const certificateBreadcrumbs = useMemo(() => {
    const courseCrumbLabel =
      course?.name?.trim() ||
      (id != null
        ? `${t('pro_course_breadcrumb_fallback', 'Corso')} ${id}`
        : t('pro_course_breadcrumb_fallback', 'Corso'));
    return [
      {
        label: t('pro_courses', 'LIBRERIA CORSI FAD ECM'),
        href: '/proCourses',
      },
      { label: courseCrumbLabel, href: backUrl },
      { label: t('pro_course_certificate_breadcrumb', 'Certificato') },
    ];
  }, [backUrl, course?.name, id, t]);

  return (
    <Container size='2xl' py='lg' style={{ position: 'relative' }}>
      <LoadingOverlay visible={loading || exporting} />

      <PageHeader
        title={t('pro_course_certificate_title', 'Certificato')}
        subtitle={t(
          'pro_course_certificate_subtitle',
          'Anteprima del certificato. Puoi salvarlo come PDF (immagine).'
        )}
        breadcrumbs={certificateBreadcrumbs}
        backUrl={backUrl}
        actions={
          <Button
            leftSection={<Download size={16} />}
            onClick={() => void handleSavePdf()}
            disabled={!eligible || loading}
          >
            {t('pro_course_certificate_save_pdf', 'Salva PDF')}
          </Button>
        }
      />

      <Divider mb='md' mt='md' />

      {eligible && course && certificateProps ? (
        <Flex justify='center' w='100%' style={{ overflow: 'auto' }}>
          <ProCourseCertificateByTemplate
            ref={certificateRef}
            {...certificateProps}
          />
        </Flex>
      ) : null}
    </Container>
  );
}
